/**
 * System notifications: what the API tells a user about their objects on the
 * current network — a line item starting or stopping, a creative rejected, a
 * report finished, a data export failing. Which ones reach the UI is set by the
 * user's notification preferences; with none saved, everything does.
 *
 * Whether a notification is read, and which have been cleared away, lives in
 * the user profile under the old UI's own keys, so the two UIs agree.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`

/** How urgent a notification is, least to most. */
export const NOTIFICATION_SEVERITIES = ['INFO', 'WARNING', 'IMPORTANT', 'ERROR'] as const
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number]

export interface NotificationMessage {
  /** Translation key, e.g. `notification.lineItem.running`. */
  code: string
  /** The API's English template, with `{{name}}`-style placeholders. */
  text?: string
  parameters?: Record<string, unknown>
}

export interface SystemNotification {
  /** Stable for the same notification across polls. */
  id: number
  /** Domain object type, e.g. `LineItem`; absent for notifications about no object. */
  objectType?: string
  object?: { id?: string; url?: string }
  message: NotificationMessage
  severity: NotificationSeverity
  time: string
}

interface ApiNotification {
  id: number
  objectType?: string
  object?: { id?: string; url?: string }
  message: NotificationMessage
  type: NotificationSeverity
  time: string
}

/** The API refuses `since` more than 30 days back; stay clear of the edge. */
const MAX_LOOKBACK_MS = 28 * 24 * 60 * 60 * 1000

/** Profile keys, shared with the old UI: per-network ISO times, keyed by network tagId. */
const SINCE_KEY = 'adnNotificationsSince'
const READ_KEY = 'adnNotificationsRead'

type NetworkTimes = Record<string, string>

export interface NotificationMarks {
  /** Notifications at or before this were cleared away. */
  since: NetworkTimes
  /** Notifications at or before this have been seen. */
  read: NetworkTimes
}

function parseTimes(raw: string | undefined): NetworkTimes {
  try {
    const value = raw ? JSON.parse(raw) : null
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  } catch {
    return {}
  }
}

/** Detail routes for the objects notifications point at. */
const OBJECT_ROUTES: Record<string, (id: string) => string> = {
  LineItem: (id) => `/advertising/lineitems/${id}`,
  Creative: (id) => `/advertising/creatives/${id}`,
  DataExport: (id) => `/admin/data-exports/${id}`,
  Site: (id) => `/publishing/sites/${id}`,
  PoliticalCampaign: (id) => `/political/campaigns/${id}`,
}

/**
 * Where a notification leads: a page in this app, an outside URL (a finished
 * report's file), or nowhere.
 */
export function notificationTarget(
  notification: SystemNotification
): { href: string; external: boolean } | null {
  const url = notification.message.parameters?.url
  if (typeof url === 'string' && url) return { href: url, external: true }
  const route = notification.objectType && OBJECT_ROUTES[notification.objectType]
  const id = notification.object?.id
  return route && id ? { href: route(id), external: false } : null
}

export const NotificationsApi = {
  /** The current network's notifications after `since` (ISO), newest first. */
  query: async (since?: string | null): Promise<SystemNotification[]> => {
    const params = new URLSearchParams({ context: getContext() })
    const floor = Date.now() - MAX_LOOKBACK_MS
    const sinceMs = since ? Date.parse(since) : NaN
    if (Number.isFinite(sinceMs)) params.set('since', new Date(Math.max(sinceMs, floor)).toISOString())
    const res = await apiFetch(`${API_BASE}/notifications?${params}`)
    if (!res.ok) await throwApiError(res, 'Failed to load notifications')
    // No network in context answers with an empty body.
    const text = await res.text()
    return toArray<ApiNotification>(text ? JSON.parse(text) : null).map(({ type, ...rest }) => ({
      ...rest,
      severity: type,
    }))
  },

  /** When this user last cleared and last read their notifications, per network. */
  marksOf: (profile: Record<string, string>): NotificationMarks => ({
    since: parseTimes(profile[SINCE_KEY]),
    read: parseTimes(profile[READ_KEY]),
  }),

  /**
   * The profile values that stamp now as the read time — and, when `clear` is
   * set, the cleared time — for one network. The stored maps cover every
   * network, so pass the profile as just re-read to keep the other networks' times.
   */
  markValues: (profile: Record<string, string>, tagId: string, clear: boolean) => {
    const now = new Date().toISOString()
    const current = NotificationsApi.marksOf(profile)
    return {
      [READ_KEY]: { ...current.read, [tagId]: now },
      ...(clear ? { [SINCE_KEY]: { ...current.since, [tagId]: now } } : {}),
    }
  },
}
